import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Crown, TrendingUp, TrendingDown, Award, Leaf, DollarSign, Trash2, CheckCircle2, Sparkles, Zap } from 'lucide-react';
import { db } from '../firebase/config';
import { collection, query, where, getDocs, limit } from 'firebase/firestore';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { format, startOfWeek, startOfMonth, subDays, subMonths, isAfter } from 'date-fns';
import { EMOJIS } from '../constants/emojis';

interface FoodItem {
  id: string;
  name: string;
  category: 'fridge' | 'freezer' | 'pantry';
  expirationDate: string;
  quantity: number;
  price: number;
  status: 'active' | 'used' | 'wasted';
  householdId: string;
  addedBy: string;
  createdAt: string;
  updatedAt: string;
}

interface AnalyticsData {
  totalSaved: number;
  totalWasted: number;
  totalItems: number;
  successRate: number;
  itemsUsed: number;
  itemsWasted: number;
  co2Saved: number;
  mealsSaved: number;
  categorySaved: { [key: string]: number };
  categoryWasted: { [key: string]: number };
  timelineData: Array<{ date: string; saved: number; wasted: number; }>;
}

const COLORS = ['#72C491', '#E63946', '#FCA311', '#457B9D', '#A8DADC'];

export function AdvancedAnalytics() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState<AnalyticsData>({
    totalSaved: 0,
    totalWasted: 0,
    totalItems: 0,
    successRate: 0,
    itemsUsed: 0,
    itemsWasted: 0,
    co2Saved: 0,
    mealsSaved: 0,
    categorySaved: {},
    categoryWasted: {},
    timelineData: [],
  });
  const [loading, setLoading] = useState(true);
  const [timePeriod, setTimePeriod] = useState<'week' | 'month' | 'all'>('month');

  useEffect(() => {
    if (!user) {
      navigate('/login', { replace: true });
      return;
    }

    loadAnalytics();
  }, [user, navigate, timePeriod]);

  async function loadAnalytics() {
    try {
      setLoading(true);

      // Get user's household
      const householdsQuery = query(
        collection(db, 'households'),
        where('members', 'array-contains', user!.uid),
        limit(1)
      );

      const householdsSnapshot = await getDocs(householdsQuery);

      if (householdsSnapshot.empty) {
        setLoading(false);
        return;
      }

      const householdId = householdsSnapshot.docs[0].id;

      // Get date filter based on time period
      const startDate = getStartDate(timePeriod);

      // Query used items
      const usedItemsQuery = query(
        collection(db, 'items'),
        where('householdId', '==', householdId),
        where('status', '==', 'used')
      );

      const usedSnapshot = await getDocs(usedItemsQuery);
      const usedItems: FoodItem[] = usedSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      } as FoodItem)).filter(item => 
        !startDate || new Date(item.updatedAt) >= startDate
      );

      // Query wasted items
      const wastedItemsQuery = query(
        collection(db, 'items'),
        where('householdId', '==', householdId),
        where('status', '==', 'wasted')
      );

      const wastedSnapshot = await getDocs(wastedItemsQuery);
      const wastedItems: FoodItem[] = wastedSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      } as FoodItem)).filter(item => 
        !startDate || new Date(item.updatedAt) >= startDate
      );

      // Calculate totals
      const totalSaved = usedItems.reduce((sum, item) => sum + (item.price || 0), 0);
      const totalWasted = wastedItems.reduce((sum, item) => sum + (item.price || 0), 0);
      const totalItems = usedItems.length + wastedItems.length;
      const successRate = totalItems > 0 ? (usedItems.length / totalItems) * 100 : 0;
      const co2Saved = usedItems.length * 2.5;
      const mealsSaved = usedItems.length;

      // Category breakdown
      const categorySaved: { [key: string]: number } = {};
      const categoryWasted: { [key: string]: number } = {};

      usedItems.forEach(item => {
        categorySaved[item.category] = (categorySaved[item.category] || 0) + (item.price || 0);
      });

      wastedItems.forEach(item => {
        categoryWasted[item.category] = (categoryWasted[item.category] || 0) + (item.price || 0);
      });

      // Timeline data
      const timelineData = generateTimelineData(usedItems, wastedItems, timePeriod);

      setAnalytics({
        totalSaved,
        totalWasted,
        totalItems,
        successRate,
        itemsUsed: usedItems.length,
        itemsWasted: wastedItems.length,
        co2Saved,
        mealsSaved,
        categorySaved,
        categoryWasted,
        timelineData,
      });

      setLoading(false);
    } catch (error) {
      console.error('Error loading analytics:', error);
      setLoading(false);
    }
  }

  function getStartDate(period: 'week' | 'month' | 'all'): Date | null {
    if (period === 'all') return null;
    if (period === 'week') return startOfWeek(new Date());
    if (period === 'month') return startOfMonth(new Date());
    return null;
  }

  function generateTimelineData(usedItems: FoodItem[], wastedItems: FoodItem[], period: 'week' | 'month' | 'all') {
    const data: Array<{ date: string; saved: number; wasted: number }> = [];
    const startDate = getStartDate(period);

    if (!startDate && period !== 'all') return data;

    // Group by date
    const groupedData: { [key: string]: { saved: number; wasted: number } } = {};

    usedItems.forEach(item => {
      const date = format(new Date(item.updatedAt), 'MMM dd');
      if (!groupedData[date]) {
        groupedData[date] = { saved: 0, wasted: 0 };
      }
      groupedData[date].saved += item.price || 0;
    });

    wastedItems.forEach(item => {
      const date = format(new Date(item.updatedAt), 'MMM dd');
      if (!groupedData[date]) {
        groupedData[date] = { saved: 0, wasted: 0 };
      }
      groupedData[date].wasted += item.price || 0;
    });

    // Convert to array
    Object.keys(groupedData).forEach(date => {
      data.push({
        date,
        saved: parseFloat(groupedData[date].saved.toFixed(2)),
        wasted: parseFloat(groupedData[date].wasted.toFixed(2)),
      });
    });

    return data.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()).slice(-10);
  }

  function getCategoryChartData() {
    const categories = ['fridge', 'freezer', 'pantry'];
    return categories.map(cat => ({
      name: cat.charAt(0).toUpperCase() + cat.slice(1),
      saved: analytics.categorySaved[cat] || 0,
      wasted: analytics.categoryWasted[cat] || 0,
    }));
  }

  function getWastePieData() {
    const data = Object.entries(analytics.categoryWasted)
      .filter(([_, value]) => value > 0)
      .map(([category, value]) => ({
        name: category.charAt(0).toUpperCase() + category.slice(1),
        value,
        percent: value / analytics.totalWasted,
      }));
    return data;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-neutral-bg via-primary-mint/10 to-neutral-bg flex items-center justify-center">
        <div className="text-center">
          <img src="/logo.png" alt="Loading" className="w-20 h-20 mx-auto mb-4 animate-pulse" />
          <p className="text-gray-600 font-medium">Loading your premium analytics...</p>
          <div className="mt-4 flex items-center justify-center gap-2">
            <Sparkles className="w-5 h-5 text-yellow-500 animate-pulse" />
            <Sparkles className="w-5 h-5 text-yellow-500 animate-pulse delay-75" />
            <Sparkles className="w-5 h-5 text-yellow-500 animate-pulse delay-150" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-bg via-primary-mint/10 to-neutral-bg">
      {/* Premium Badge Banner - Animated */}
      <div className="bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer"></div>
        <div className="container mx-auto px-4 py-4 relative z-10">
          <div className="flex items-center justify-center gap-3">
            <Crown className="w-6 h-6 text-white animate-bounce" />
            <span className="font-black text-lg tracking-wider text-white drop-shadow-lg">PREMIUM ANALYTICS</span>
            <Crown className="w-6 h-6 text-white animate-bounce" style={{ animationDelay: '0.2s' }} />
          </div>
        </div>
      </div>

      {/* Header */}
      <header className="bg-white/80 backdrop-blur-lg border-b border-gray-200 sticky top-0 z-40 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link
                to="/dashboard"
                className="p-2 hover:bg-gray-100 rounded-xl transition-all hover:scale-105"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </Link>
              <div className="flex items-center gap-3">
                <img src="/logo.png" alt="FridgeLife" className="w-10 h-10" />
                <h1 className="text-2xl font-bold bg-gradient-to-r from-primary-green to-primary-mint bg-clip-text text-transparent">
                  Advanced Analytics
                </h1>
              </div>
            </div>
            <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-yellow-50 rounded-full border border-yellow-200">
              <Sparkles className="w-4 h-4 text-yellow-600" />
              <span className="text-xs font-semibold text-yellow-700">Premium</span>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-7xl mx-auto">
          {/* Time Period Filter - Enhanced */}
          <div className="flex gap-3 mb-8 flex-wrap">
            {[
              { value: 'week', label: 'This Week', icon: Zap },
              { value: 'month', label: 'This Month', icon: TrendingUp },
              { value: 'all', label: 'All Time', icon: Award }
            ].map(({ value, label, icon: Icon }) => (
              <button
                key={value}
                onClick={() => setTimePeriod(value as 'week' | 'month' | 'all')}
                className={`group px-6 py-3 rounded-xl font-semibold text-sm sm:text-base transition-all duration-300 transform hover:scale-105 flex items-center gap-2 ${
                  timePeriod === value
                    ? 'bg-gradient-to-r from-primary-green to-primary-mint text-white shadow-lg shadow-primary-green/30'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200 hover:border-primary-green/50'
                }`}
              >
                <Icon className={`w-4 h-4 ${timePeriod === value ? 'animate-pulse' : ''}`} />
                {label}
              </button>
            ))}
          </div>

          {/* Key Metrics - Enhanced with Gradients and Animations */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {/* Money Saved */}
            <div className="group bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl shadow-lg hover:shadow-2xl p-6 border border-green-100 transform hover:scale-105 transition-all duration-300">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-gradient-to-br from-safe to-green-500 rounded-xl shadow-lg">
                  <DollarSign className="w-8 h-8 text-white" />
                </div>
                <TrendingUp className="w-6 h-6 text-safe animate-bounce" />
              </div>
              <div className="text-3xl md:text-4xl font-black text-gray-900 mb-1">
                ${analytics.totalSaved.toFixed(2)}
              </div>
              <div className="text-base font-bold text-gray-700 mb-2">Money Saved</div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <CheckCircle2 className="w-4 h-4 text-safe" />
                <span>{analytics.itemsUsed} items used</span>
              </div>
            </div>

            {/* Money Wasted */}
            <div className="group bg-gradient-to-br from-red-50 to-pink-50 rounded-2xl shadow-lg hover:shadow-2xl p-6 border border-red-100 transform hover:scale-105 transition-all duration-300">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-gradient-to-br from-critical to-red-500 rounded-xl shadow-lg">
                  <Trash2 className="w-8 h-8 text-white" />
                </div>
                <TrendingDown className="w-6 h-6 text-critical" />
              </div>
              <div className="text-3xl md:text-4xl font-black text-gray-900 mb-1">
                ${analytics.totalWasted.toFixed(2)}
              </div>
              <div className="text-base font-bold text-gray-700 mb-2">Money Wasted</div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Trash2 className="w-4 h-4 text-critical" />
                <span>{analytics.itemsWasted} items wasted</span>
              </div>
            </div>

            {/* Success Rate */}
            <div className="group bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl shadow-lg hover:shadow-2xl p-6 border border-blue-100 transform hover:scale-105 transition-all duration-300">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-gradient-to-br from-primary-green to-blue-500 rounded-xl shadow-lg">
                  <Award className="w-8 h-8 text-white" />
                </div>
                <CheckCircle2 className="w-6 h-6 text-primary-green animate-pulse" />
              </div>
              <div className="text-3xl md:text-4xl font-black text-gray-900 mb-1">
                {Math.round(analytics.successRate)}%
              </div>
              <div className="text-base font-bold text-gray-700 mb-2">Success Rate</div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Award className="w-4 h-4 text-primary-green" />
                <span>{analytics.totalItems} total items</span>
              </div>
            </div>

            {/* Environmental Impact */}
            <div className="group bg-gradient-to-br from-teal-50 to-green-50 rounded-2xl shadow-lg hover:shadow-2xl p-6 border border-teal-100 transform hover:scale-105 transition-all duration-300">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-gradient-to-br from-primary-mint to-teal-500 rounded-xl shadow-lg">
                  <Leaf className="w-8 h-8 text-white" />
                </div>
                <span className="text-3xl animate-pulse">{EMOJIS.PLANT}</span>
              </div>
              <div className="text-3xl md:text-4xl font-black text-gray-900 mb-1">
                {Math.round(analytics.co2Saved)} lbs
              </div>
              <div className="text-base font-bold text-gray-700 mb-2">CO₂ Avoided</div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Leaf className="w-4 h-4 text-primary-mint" />
                <span>{analytics.mealsSaved} meals saved</span>
              </div>
            </div>
          </div>

          {/* Charts Row 1 - Enhanced */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Money Over Time */}
            <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl hover:shadow-2xl p-6 border border-gray-100 transition-all duration-300 transform hover:scale-[1.02]">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900">Money Saved vs Wasted</h3>
                <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 rounded-full">
                  <TrendingUp className="w-4 h-4 text-primary-green" />
                  <span className="text-xs font-semibold text-gray-700">Trends</span>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={320}>
                <LineChart data={analytics.timelineData}>
                  <defs>
                    <linearGradient id="colorSaved" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#72C491" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#72C491" stopOpacity={0.1}/>
                    </linearGradient>
                    <linearGradient id="colorWasted" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#E63946" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#E63946" stopOpacity={0.1}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="date" tick={{ fontSize: 12, fill: '#6b7280' }} />
                  <YAxis tick={{ fontSize: 12, fill: '#6b7280' }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                      border: 'none', 
                      borderRadius: '12px', 
                      boxShadow: '0 10px 25px rgba(0,0,0,0.1)' 
                    }}
                  />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="saved" 
                    stroke="#72C491" 
                    strokeWidth={3} 
                    name="Saved ($)" 
                    dot={{ fill: '#72C491', strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="wasted" 
                    stroke="#E63946" 
                    strokeWidth={3} 
                    name="Wasted ($)" 
                    dot={{ fill: '#E63946', strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Category Breakdown */}
            <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl hover:shadow-2xl p-6 border border-gray-100 transition-all duration-300 transform hover:scale-[1.02]">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900">Savings by Category</h3>
                <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 rounded-full">
                  <Award className="w-4 h-4 text-primary-green" />
                  <span className="text-xs font-semibold text-gray-700">Breakdown</span>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={getCategoryChartData()}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#6b7280' }} />
                  <YAxis tick={{ fontSize: 12, fill: '#6b7280' }} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                      border: 'none', 
                      borderRadius: '12px', 
                      boxShadow: '0 10px 25px rgba(0,0,0,0.1)' 
                    }}
                  />
                  <Legend />
                  <Bar dataKey="saved" fill="#72C491" name="Saved ($)" radius={[8, 8, 0, 0]} />
                  <Bar dataKey="wasted" fill="#E63946" name="Wasted ($)" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Charts Row 2 - Enhanced */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Waste by Category Pie */}
            {getWastePieData().length > 0 && (
              <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl hover:shadow-2xl p-6 border border-gray-100 transition-all duration-300 transform hover:scale-[1.02]">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold text-gray-900">Waste Distribution</h3>
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 rounded-full">
                    <Trash2 className="w-4 h-4 text-critical" />
                    <span className="text-xs font-semibold text-gray-700">Analysis</span>
                  </div>
                </div>
                <ResponsiveContainer width="100%" height={320}>
                  <PieChart>
                    <Pie
                      data={getWastePieData()}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={(entry: any) => `${entry.name}: ${(entry.percent * 100).toFixed(0)}%`}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {getWastePieData().map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'rgba(255, 255, 255, 0.95)', 
                        border: 'none', 
                        borderRadius: '12px', 
                        boxShadow: '0 10px 25px rgba(0,0,0,0.1)' 
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Impact Summary - Enhanced with Gradient */}
            <div className="bg-gradient-to-br from-primary-green via-teal-600 to-primary-mint rounded-3xl shadow-xl hover:shadow-2xl p-6 text-white relative overflow-hidden transition-all duration-300 transform hover:scale-[1.02]">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32"></div>
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-white/10 rounded-full -ml-32 -mb-32"></div>
              
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-2xl font-black">Your Environmental Impact</h3>
                  <Leaf className="w-8 h-8 animate-pulse" />
                </div>
                
                <div className="space-y-5">
                  <div className="flex items-center gap-4 bg-white/10 backdrop-blur-sm rounded-2xl p-4 hover:bg-white/20 transition-all">
                    <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Leaf className="w-7 h-7" />
                    </div>
                    <div>
                      <div className="text-2xl font-black">{Math.round(analytics.co2Saved)} lbs CO₂</div>
                      <div className="text-sm text-white/90 font-medium">Carbon emissions avoided</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 bg-white/10 backdrop-blur-sm rounded-2xl p-4 hover:bg-white/20 transition-all">
                    <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center text-2xl flex-shrink-0">
                      <span className="text-3xl">{EMOJIS.FOOD}</span>
                    </div>
                    <div>
                      <div className="text-2xl font-black">{analytics.mealsSaved} meals</div>
                      <div className="text-sm text-white/90 font-medium">Saved from waste</div>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 bg-white/10 backdrop-blur-sm rounded-2xl p-4 hover:bg-white/20 transition-all">
                    <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center text-2xl flex-shrink-0">
                      <span className="text-3xl">{EMOJIS.WATER_DROP}</span>
                    </div>
                    <div>
                      <div className="text-2xl font-black">{Math.round(analytics.mealsSaved * 50)} gal</div>
                      <div className="text-sm text-white/90 font-medium">Water conserved</div>
                    </div>
                  </div>

                  <div className="mt-6 pt-6 border-t border-white/20">
                    <div className="flex items-center gap-2 text-white/90">
                      <Sparkles className="w-5 h-5 animate-pulse" />
                      <p className="text-sm font-medium">
                        Keep up the great work! Every item you save helps reduce your environmental footprint.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Tips Section - Enhanced */}
          {analytics.successRate < 70 && (
            <div className="mt-8 bg-gradient-to-br from-yellow-50 to-orange-50 rounded-3xl shadow-xl p-6 border-l-4 border-caution transform hover:scale-[1.01] transition-all duration-300">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-yellow-100 rounded-xl">
                  <span className="text-2xl">{EMOJIS.LIGHTBULB}</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900">Tips to Improve Your Success Rate</h3>
              </div>
              <ul className="space-y-3 text-gray-700">
                {[
                  'Check your fridge regularly to use items before they expire',
                  'Plan meals around items expiring soon',
                  'Freeze items if you won\'t use them in time',
                  'Buy smaller quantities to reduce waste'
                ].map((tip, index) => (
                  <li key={index} className="flex items-start gap-3 bg-white/60 rounded-xl p-3 hover:bg-white/80 transition-all">
                    <span className="text-primary-green mt-0.5 flex-shrink-0">{EMOJIS.CHECK}</span>
                    <span className="font-medium">{tip}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .animate-shimmer {
          animation: shimmer 3s infinite;
        }
      `}</style>
    </div>
  );
}
